import React, {useEffect, useMemo, useState} from 'react'
import { Box, Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import { useForm, Controller } from "react-hook-form";
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import DateAdapter from '@mui/lab/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { parse, } from 'date-fns';
import { saveExpense, getExpenseTypes } from 'services/expenses';
import { getConstDbFormat } from 'utils/common';

const IssueDetails = ({libraryId, expenseDetails, onExpenseUpdated}) => {

  const defaultFormFields = useMemo(() => ({
    type: '',
    amount: 0,
    date: new Date(),
    comments: '',
    id: null,
    propertyId: libraryId,
    propertyType: 'LIBRARY'
  }),[libraryId]);

  const { enqueueSnackbar } = useSnackbar();
  const [expenseTypes, setExpenseTypes] = useState([]);
  const { handleSubmit, control, formState: { errors }, reset } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  }); 

  useEffect(() => {
    getExpenseTypeData()
  }, []);

  const getExpenseTypeData = (data) => {
    getExpenseTypes().then(({response, error}) => {
      if(response){
        setExpenseTypes([...response]);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }
  
  useEffect(() => {
    if(expenseDetails){
      const data = {...(Object.fromEntries(Object.entries(defaultFormFields).map(([key, value]) => {
        if(key === 'date'){
         return[key, parse(expenseDetails[key], 'dd-MM-yyyy', new Date())];
        }
        return [key, expenseDetails[key] || value];
      })))};      
      data['id'] = expenseDetails?.id || null;
     
      reset({...data});
    }
  }, [expenseDetails, defaultFormFields, reset]);

  const onSubmit = (data) => {
    console.log('data', data);
    saveExpense({
      ...data,
      date: getConstDbFormat(data?.date),
      ...(data?.id ? {id: data.id} : {}),
    }).then(({response, error}) => {
      if(response){
        enqueueSnackbar((response?.message || `Expense ${expenseDetails?.id ? 'updated' : 'added'} successfully`), {variant: 'success'});
        onExpenseUpdated();
        reset({...defaultFormFields});
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  return (
    <Box  sx={{ mt: 2 }} noValidate component="form"  onSubmit={handleSubmit(onSubmit)}>
      <Grid container columnSpacing={2} rowSpacing={4} >
        <Grid item xs={12} sm={12} md={12} lg={12} >
          <Grid container columnSpacing={2} justifyContent="center">
            <Grid item xs={12} sm={10} md={10} lg={10} sx={{ mt: 3 }}>
              <Controller
                control={control}
                name="type"
                id="type"
                required
                rules={{ required: "Select expense type" }}
                render={({field}) => (
                  <FormControl {...field} required fullWidth error={!!errors?.type}>
                    <InputLabel id="expense_type_label" size="small">Expense Type</InputLabel>
                    <Select
                      labelId="expense_type_label"
                      id="expense_type-id"
                      label="Expense Type"
                      name="type"
                      required
                      size="small"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {expenseTypes.map((type,index)=><MenuItem key={index} value={type}>{type}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={10} md={10} lg={10} >
              <Controller
                control={control}
                name="amount"
                id="amount"
                required
                rules={{ required: "Enter amount" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    type="number"
                    margin="normal"
                    label="Amount"
                    required
                    error={!!errors?.amount}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={10} md={10} lg={10} xl={10} sx={{mt: 2}}>
              <Controller
                control={control}
                name="date"
                id="date"
                required
                rules={{ required: "Select expense date",
                  // validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date",
                  // validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
                }}
                render={({field}) => (
                  <LocalizationProvider dateAdapter={DateAdapter} >
                    <DatePicker
                      {...field}
                      required
                      label="Expense date"
                      inputFormat="dd/MM/yyyy"
                      name="date"
                      clearable={false}
                      // minDate={new Date()}
                      value={field.value}
                      onChange={field.onChange}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          required
                          value={field.value}
                          error={!!errors?.date}  
                          size="small" 
                          sx={{width: '100%'}} 
                          onKeyDown={(e) =>{ e.preventDefault(); return false;}}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={10} md={10} lg={10} >
              <Controller
                control={control}
                name="comments"
                id="comments"
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Comments"
                    error={!!errors?.comments}
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                  />
                )}
              />
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
                startIcon={ expenseDetails ? <ModeEditIcon />: <ControlPointIcon />}
                color="primary"
              >
                {expenseDetails ? 'Update Expense' : 'Add Expense'}
              </Button>
            </Grid>


          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(IssueDetails);
